import { Button } from "@/components/flexkit/Button";
import { Label } from "@/components/podkit/forms/Label";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { useFlag } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";

export const AgentPolicies: FC = () => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();
    const { value: scmToolsFeatureEnabled } = useFlag(Flags.ScmToolsEnabled, false);

    const [mcpEnabled, setMcpEnabled] = useState(true);
    const [scmToolsEnabled, setScmToolsEnabled] = useState(true);
    const [denyList, setDenyList] = useState("");
    const [isDenyListDirty, setIsDenyListDirty] = useState(false);

    // Initialize state from API data
    useEffect(() => {
        if (organizationPolicies?.agentPolicy) {
            setMcpEnabled(!organizationPolicies.agentPolicy.mcpDisabled);
            setScmToolsEnabled(!organizationPolicies.agentPolicy.scmToolsDisabled);
            setDenyList(organizationPolicies.agentPolicy.commandDenyList.join("\n"));
        }
    }, [organizationPolicies]);

    const handleMcpToggle = useCallback(
        async (checked: boolean) => {
            const prevState = mcpEnabled;
            setMcpEnabled(checked);

            const params: PartialMessage<OrganizationPolicies> = {
                agentPolicy: {
                    mcpDisabled: !checked,
                    scmToolsDisabled: !scmToolsEnabled,
                    commandDenyList: denyList.split("\n").filter((line) => line.trim()),
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: checked ? "MCP enabled" : "MCP disabled",
                });
            } catch (error) {
                console.error(error);
                setMcpEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [mcpEnabled, scmToolsEnabled, denyList, updateOrganizationPolicies, toast],
    );

    const handleScmToolsToggle = useCallback(
        async (checked: boolean) => {
            const prevState = scmToolsEnabled;
            setScmToolsEnabled(checked);

            const params: PartialMessage<OrganizationPolicies> = {
                agentPolicy: {
                    mcpDisabled: !mcpEnabled,
                    scmToolsDisabled: !checked,
                    commandDenyList: denyList.split("\n").filter((line) => line.trim()),
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: checked ? "SCM tools enabled" : "SCM tools disabled",
                });
            } catch (error) {
                console.error(error);
                setScmToolsEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [mcpEnabled, scmToolsEnabled, denyList, updateOrganizationPolicies, toast],
    );

    const handleDenyListChange = useCallback((value: string) => {
        setDenyList(value);
        setIsDenyListDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        const params: PartialMessage<OrganizationPolicies> = {
            agentPolicy: {
                mcpDisabled: !mcpEnabled,
                scmToolsDisabled: !scmToolsEnabled,
                commandDenyList: denyList.split("\n").filter((line) => line.trim()),
            },
        };

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            setIsDenyListDirty(false);
            toast({
                title: "Command deny list updated",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [mcpEnabled, scmToolsEnabled, denyList, updateOrganizationPolicies, toast]);

    return (
        <div className="flex max-w-[455px] flex-col gap-4">
            <Heading3>Policies</Heading3>
            <SkeletonBlock ready={!isLoading}>
                <div className="flex flex-col gap-6" data-testid="agent-policies-section">
                    <Text className="text-base text-content-secondary">
                        Configure security policies and restrictions for AI agents in your organization.
                    </Text>

                    {/* MCP Toggle */}
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-4">
                            <div className="flex">
                                <Switch
                                    state={mcpEnabled ? "checked" : "unchecked"}
                                    onToggle={handleMcpToggle}
                                    id="mcp-toggle"
                                    aria-label="Enable Model Context Protocol (MCP)"
                                />
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-1">
                                    <Label className="text-base">Model Context Protocol (MCP)</Label>
                                    <Text className="text-sm text-content-secondary">
                                        Allow agents to use MCP servers for extended functionality
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SCM Tools Toggle - only shown when feature flag is enabled */}
                    {scmToolsFeatureEnabled && (
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-4">
                                <div className="flex">
                                    <Switch
                                        state={updateOrganizationPolicies.isPending ? "pending" : scmToolsEnabled ? "checked" : "unchecked"}
                                        onToggle={handleScmToolsToggle}
                                        id="scm-tools-toggle"
                                        aria-label="Enable SCM Tools"
                                    />
                                </div>
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1">
                                        <Label className="text-base">SCM Tools</Label>
                                        <Text className="text-sm text-content-secondary">
                                            Allow agents to interact with source control management systems (GitHub, GitLab, etc.)
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Command Deny List */}
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <Label className="text-base font-medium">Command Deny List</Label>
                            <Text className="text-sm text-content-secondary">
                                Enter commands or patterns that should be blocked (one per line)
                            </Text>
                        </div>
                        <Textarea
                            value={denyList}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                handleDenyListChange(e.target.value)
                            }
                            placeholder="rm -rf&#10;sudo&#10;curl&#10;wget"
                            minHeight={120}
                            maxHeight={400}
                            className="resize-none font-mono text-sm"
                            data-testid="deny-list-textarea"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <Button
                            onClick={handleSave}
                            variant="primary"
                            disabled={!isDenyListDirty}
                            loading={updateOrganizationPolicies.isPending}
                            data-testid="save-policies-button"
                            data-tracking-id="save-changes-agent-policies"
                        >
                            Save Changes
                        </Button>
                    </div>
                </div>
            </SkeletonBlock>
        </div>
    );
};
